
























































import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import { ProductsApi, Vat, VatsApi } from '@/api';
import Toast from '@/components/display/toast.vue';
import { ApiUtils } from '@/common/ApiUtils';

@Component({})
export default class RenumberVatsModal extends Vue {
	@Prop({ default: 'renumber-vats-modal' })
	public modalId!: string;

	@Prop({ required: true, type: Array })
	public vats!: Vat[];

	@Prop({ required: true, type: Number })
	public productId!: number;

	@Prop({ type: Number })
	public totalVatCount?: number;

	public editableVats: Vat[] = [];

	public get maxVatNumber(): number {
		return 4;
	}

	public resetForm() {
		this.editableVats = this.vats.map(vat => ({ ...vat }));
	}

	public hasNumber(vat: Vat): boolean {
		return typeof vat.vatNumber === 'number' && Number.isFinite(vat.vatNumber);
	}

	public get numberCounts(): Map<number, number> {
		const counts = new Map<number, number>();
		this.editableVats.forEach(vat => {
			if (this.hasNumber(vat)) {
				counts.set(vat.vatNumber!, (counts.get(vat.vatNumber!) ?? 0) + 1);
			}
		});
		return counts;
	}

	public get missingRangeLength(): number {
		const highestAssigned = Math.max(0, ...Array.from(this.numberCounts.keys()));
		return Math.min(Math.max(this.editableVats.length, highestAssigned), this.maxVatNumber);
	}

	public get missingNumbers(): number[] {
		return Array.from({ length: this.missingRangeLength }, (_, i) => i + 1).filter(
			vatNumber => !this.numberCounts.has(vatNumber),
		);
	}

	public get duplicateNumbers(): number[] {
		return Array.from(this.numberCounts.entries())
			.filter(([, count]) => count > 1)
			.map(([vatNumber]) => vatNumber);
	}

	public get hasNumberingWarnings(): boolean {
		return this.missingNumbers.length > 0 || this.duplicateNumbers.length > 0;
	}

	public get canSave(): boolean {
		return (
			this.duplicateNumbers.length === 0 &&
			this.editableVats.every(
				vat => this.hasNumber(vat) && !this.isOutOfRange(vat) && !this.isNotInteger(vat),
			)
		);
	}

	public get alertVariant(): string {
		return this.duplicateNumbers.length > 0 ? 'danger' : 'warning';
	}

	public isDuplicate(vat: Vat): boolean {
		return this.hasNumber(vat) && (this.numberCounts.get(vat.vatNumber!) ?? 0) > 1;
	}

	public isOutOfRange(vat: Vat): boolean {
		return this.hasNumber(vat) && (vat.vatNumber! < 1 || vat.vatNumber! > this.maxVatNumber);
	}

	public isNotInteger(vat: Vat): boolean {
		return this.hasNumber(vat) && !Number.isInteger(vat.vatNumber);
	}

	public blockDecimalKey(event: KeyboardEvent) {
		if (event.key === '.') {
			event.preventDefault();
		}
	}

	@Emit('saved')
	public onSaved(): void {}

	public async submit(event: Event): Promise<void> {
		event.preventDefault();
		if (!this.canSave) return;
		try {
			await new ProductsApi().productVatLabels(this.productId, {
				data: {
					vats: this.editableVats.map(vat => ({
						vatId: vat.id,
						vatNumber: vat.vatNumber ?? undefined,
					})),
				},
			});
			// productVatLabels resets each vat's label as a side effect of setting its number,
			// so every label must be reasserted here, not just the ones edited in this modal.
			await Promise.all(
				this.editableVats.map(vat =>
					new VatsApi().updateVat(vat.id, { data: { label: vat.label } }),
				),
			);
			new Toast().makeToast('Vats renumbered successfully', 'Success', 'success');
			this.$bvModal.hide(this.modalId);
			this.onSaved();
		} catch (e) {
			const { errorMessage } = ApiUtils.handleApiError(e, 'There was an issue renumbering vats');
			new Toast().makeToast(errorMessage, 'Error', 'danger');
		}
	}
}
